/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useMemo, useState } from 'react';
import dayjs from '@/lib/dayjs';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import type { StoreDayInfo, GetStoreDetailsData } from '../interface/types';
import type { YextHolidayHour } from '../interface/yext-types';
import { type Locale } from '@/types/i18n';

const JS_DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

const labelMap = {
  lblOpenNow: 'label_locations_storeStatus_open',
  lblClosed: 'label_locations_storeStatus_closed',
  lblOpensAt: 'label_locations_storeStatus_opensAt',
  lblClosesAt: 'label_locations_storeStatus_closesAt',
  lblToday: 'label_StoreLocator_modal_TODAY',
  lblTomorrow: 'label_StoreLocator_modal_Tomorrow',
};

type UseStoreDayInfoParams = {
  hours: GetStoreDetailsData['hours'] | undefined;
  closed: GetStoreDetailsData['closed'] | undefined;
  timezone: GetStoreDetailsData['timezone'] | undefined;
  holidayHours?: YextHolidayHour[];
};

type UseStoreDayInfoResult = {
  today: StoreDayInfo | null;
  nextOpenDay: StoreDayInfo | null;
  isOpen: boolean;
  isHolidayToday: boolean;
  statusText: string;
  hoursText: string;
};

const formatTime = (hour: number, min: number, locale: Locale): string => {
  const date = dayjs().hour(hour).minute(min);

  if (locale === 'en-US' || locale === 'es-MX') {
    return date.format('h:mm A');
  } else {
    return date.format('HH:mm');
  }
};

/**
 * Hook to calculate store day information and current open/closed status.
 *
 * IMPORTANT: This calculation MUST be performed client-side because it depends on the current
 * time at the user's location. Server-side rendering would use the server's time, which could
 * be in a different timezone and produce incorrect results.
 *
 * The hook calculates:
 * - Today's store hours
 * - Whether the store is currently open (based on real-time comparison)
 * - Next available open day (if the store is currently closed)
 * - Localized status text ("Open Now" or "Closed")
 * - Localized hours text ("Closes at X" or "Opens at X")
 */
export function useStoreDayInfo({
  hours,
  closed,
  timezone: storeTimezone,
  holidayHours,
}: UseStoreDayInfoParams): UseStoreDayInfoResult {
  const locale = useLocale();
  const labels = useLabels(labelMap);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return useMemo((): UseStoreDayInfoResult => {
    const empty: UseStoreDayInfoResult = {
      today: null,
      nextOpenDay: null,
      isOpen: false,
      isHolidayToday: false,
      statusText: '',
      hoursText: '',
    };

    if (!isClient || !hours) {
      return empty;
    }

    const getTzTime = () => (storeTimezone ? dayjs().tz(storeTimezone) : dayjs());

    const now = getTzTime();
    const currentDayIndex = now.day();
    const currentDayName = JS_DAYS_OF_WEEK[currentDayIndex];
    const regularTodayHours = hours[currentDayName];

    const todayDateStr = now.format('YYYY-MM-DD');
    const todayHoliday = holidayHours?.find((h) => h.date === todayDateStr);
    const hasHolidayToday = Boolean(todayHoliday && !todayHoliday.isRegularHours);

    const todayHours = hasHolidayToday
      ? {
          isClosed: todayHoliday?.isClosed,
          openIntervals: todayHoliday?.openIntervals,
        }
      : regularTodayHours;

    const today: StoreDayInfo | null = todayHours
      ? { day: currentDayName, hours: todayHours }
      : null;

    let isOpen = false;
    let statusText = labels.lblClosed;
    let hoursText = '';

    if (
      !closed &&
      todayHours &&
      !todayHours.isClosed &&
      todayHours.openIntervals &&
      todayHours.openIntervals.length > 0
    ) {
      let currentInterval = null;
      let nextInterval = null;

      for (const interval of todayHours.openIntervals) {
        const [openHour, openMin] = interval.start.split(':').map(Number);
        const [closeHour, closeMin] = interval.end.split(':').map(Number);

        const openTime = getTzTime().hour(openHour).minute(openMin).second(0);

        const isMidnightClose = closeHour === 0 && closeMin === 0;
        const closeTime = isMidnightClose
          ? getTzTime().add(1, 'day').hour(closeHour).minute(closeMin).second(0)
          : getTzTime().hour(closeHour).minute(closeMin).second(0);

        if ((now.isAfter(openTime) || now.isSame(openTime)) && now.isBefore(closeTime)) {
          isOpen = true;
          currentInterval = { start: interval.start, end: interval.end, closeHour, closeMin };
          break;
        }

        if (now.isBefore(openTime) && !nextInterval) {
          nextInterval = { start: interval.start, openHour, openMin };
        }
      }

      if (isOpen && currentInterval) {
        const closingTime = formatTime(currentInterval.closeHour, currentInterval.closeMin, locale);
        statusText = labels.lblOpenNow;
        hoursText = `${labels.lblClosesAt} ${closingTime}`;
      } else if (nextInterval) {
        const openingTime = formatTime(nextInterval.openHour, nextInterval.openMin, locale);
        statusText = labels.lblClosed;
        hoursText = `${labels.lblOpensAt} ${openingTime} ${labels.lblToday}`;
      } else {
        const tomorrowIndex = (currentDayIndex + 1) % 7;
        const tomorrowName = JS_DAYS_OF_WEEK[tomorrowIndex];
        const tomorrowDateStr = now.add(1, 'day').format('YYYY-MM-DD');
        const tomorrowHoliday = holidayHours?.find((h) => h.date === tomorrowDateStr);
        const hasTomorrowHoliday = Boolean(tomorrowHoliday && !tomorrowHoliday.isRegularHours);
        const tomorrowHours = hasTomorrowHoliday
          ? {
              isClosed: tomorrowHoliday?.isClosed,
              openIntervals: tomorrowHoliday?.openIntervals,
            }
          : hours[tomorrowName];

        if (
          tomorrowHours &&
          !tomorrowHours.isClosed &&
          tomorrowHours.openIntervals &&
          tomorrowHours.openIntervals.length > 0
        ) {
          const firstInterval = tomorrowHours.openIntervals[0];
          const [openHour, openMin] = firstInterval.start.split(':').map(Number);
          hoursText = `${labels.lblOpensAt} ${formatTime(openHour, openMin, locale)} ${labels.lblTomorrow}`;
        }
      }
    }

    let nextOpenDay: StoreDayInfo | null = null;

    if (
      closed ||
      !todayHours ||
      todayHours.isClosed ||
      !todayHours.openIntervals ||
      todayHours.openIntervals.length === 0
    ) {
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (currentDayIndex + i) % 7;
        const nextDayName = JS_DAYS_OF_WEEK[nextDayIndex];
        const nextDayHours = hours[nextDayName];

        if (
          nextDayHours &&
          !nextDayHours.isClosed &&
          nextDayHours.openIntervals &&
          nextDayHours.openIntervals.length > 0
        ) {
          nextOpenDay = { day: nextDayName, hours: nextDayHours };
          break;
        }
      }
    }

    return { today, nextOpenDay, isOpen, isHolidayToday: hasHolidayToday, statusText, hoursText };
  }, [isClient, hours, closed, labels, locale, storeTimezone, holidayHours]);
}
